import { useMemo, useState } from "react";
import { Skeleton, CustomPopover, EmptyState } from "../../shared";
import { cn } from "../../../lib/cn";
import {
  HiOutlineDocumentText,
  HiOutlineArrowDownTray,
  HiOutlineEye,
  HiOutlineShare,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowsUpDown,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineEllipsisVertical,
  HiOutlineChevronUp,
  HiOutlineChevronDown,
  HiOutlinePhoto,
  HiOutlineTableCells,
  HiOutlineFolderOpen,
  HiOutlineFolder,
  HiOutlinePencil,
  HiOutlineArrowsRightLeft,
  HiOutlineArrowRight,
} from "react-icons/hi2";
import { getFileTypePalette } from "../../../constants/fileTypeStyles";

const TYPE_OPTIONS = [
  { key: "", label: "All" },
  { key: "PDF", label: "Documents" },
  { key: "IMAGE", label: "Images" },
  { key: "OFFICE", label: "Spreadsheets" },
  { key: "OTHER", label: "Other" },
];

const SORT_OPTIONS = [
  { label: "Newest", field: "created_at", order: "desc" },
  { label: "Oldest", field: "created_at", order: "asc" },
  { label: "Name A-Z", field: "original_name", order: "asc" },
  { label: "Name Z-A", field: "original_name", order: "desc" },
  { label: "Size small", field: "size_bytes", order: "asc" },
  { label: "Size large", field: "size_bytes", order: "desc" },
];

const TABLE_COLUMNS = [
  { key: "original_name", label: "NAME", sortable: true },
  { key: "category", label: "TYPE", sortable: false },
  { key: "size_bytes", label: "SIZE", sortable: true },
  { key: "created_at", label: "UPLOADED", sortable: true },
  { key: "uploaded_by", label: "UPLOADED BY", sortable: false },
  { key: "actions", label: "ACTIONS", sortable: false },
];

const buildPageWindow = (page, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages = new Set([1, totalPages, page, page - 1, page + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const result = [];
  sorted.forEach((p, idx) => {
    if (idx > 0 && p - sorted[idx - 1] > 1) result.push("...");
    result.push(p);
  });
  return result;
};

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/New_York" });
};

const getFileIconMeta = (category) => {
  const iconMap = {
    IMAGE: HiOutlinePhoto,
    PDF: HiOutlineDocumentText,
    OFFICE: HiOutlineTableCells,
  };

  return {
    Icon: iconMap[category] || HiOutlineDocumentText,
    palette: getFileTypePalette(category),
  };
};

const FileIcon = ({ category }) => {
  const { Icon, palette } = getFileIconMeta(category);
  return (
    <div className={cn("w-9 h-10 rounded-lg flex items-center justify-center border", palette)}>
      <Icon className="w-4 h-4" />
    </div>
  );
};

const RowActionsMenu = ({ file, permissions, onPreview, onDownload, onShare, onDelete, onMove, onRename, secondaryItems = [], itemKind = "file" }) => {
  const canShare = permissions?.can_share;
  const canDelete = permissions?.can_delete;
  const canManage = permissions?.can_manage_folders !== false;
  const isFolder = itemKind === "folder";
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <CustomPopover
      open={menuOpen}
      setOpen={setMenuOpen}
      placement="bottom-end"
      trigger={
        <button
          data-row-action
          className="ml-auto p-1.5 rounded-md text-grey hover:bg-surface-active hover:text-darkgrey transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          aria-label={`More actions for ${file.original_name}`}
        >
          <HiOutlineEllipsisVertical className="w-4 h-4" />
        </button>
      }
    >
      <div className="flex flex-col py-1 min-w-[160px]">
        {!isFolder && (
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlineEye className="w-4 h-4" />
            Preview
          </button>
        )}
        {isFolder && onPreview && (
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlineArrowRight className="w-4 h-4" />
            Open
          </button>
        )}
        {onDownload && (
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlineArrowDownTray className="w-4 h-4" />
            Download
          </button>
        )}
        {canShare && onShare && (
          <button
            onClick={(e) => { e.stopPropagation(); onShare(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlineShare className="w-4 h-4" />
            Share
          </button>
        )}
        {canManage && onMove && (
          <button
            onClick={(e) => { e.stopPropagation(); onMove(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlineArrowsRightLeft className="w-4 h-4" />
            Move
          </button>
        )}
        {canManage && onRename && (
          <button
            onClick={(e) => { e.stopPropagation(); onRename(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlinePencil className="w-4 h-4" />
            Rename
          </button>
        )}
        {secondaryItems.map((item) => (
          <button
            key={item.key}
            onClick={(e) => { e.stopPropagation(); item.onClick(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors cursor-pointer flex items-center gap-2"
          >
            {item.icon}
            {item.label}
          </button>
        ))}
        {canDelete && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(file); setMenuOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-sans text-danger hover:bg-danger/5 transition-colors cursor-pointer flex items-center gap-2"
          >
            <HiOutlineTrash className="w-4 h-4" />
            Delete
          </button>
        )}
      </div>
    </CustomPopover>
  );
};

const MobileFileCard = ({ file, permissions, onPreview, onShare, onDelete, onDownload, onSelect, isSelected, onMove, onRename }) => {
  return (
    <article
      onClick={() => onSelect?.(file)}
      className={cn(
        "w-full overflow-hidden rounded-xl border bg-white p-3 shadow-sm transition-colors",
        isSelected ? "border-primary/30 ring-1 ring-primary/10" : "border-surface-border"
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreview(file);
          }}
          className="shrink-0"
          aria-label={`Preview ${file.original_name}`}
        >
          <FileIcon category={file.category} />
        </button>
        <div className="min-w-0 flex-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(file);
            }}
            className="block w-full text-left"
            aria-label={`Open ${file.original_name}`}
          >
            <p className="text-sm font-semibold text-darkgrey truncate">{file.original_name}</p>
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-grey">
            <span className="inline-flex items-center rounded-full border border-surface-border bg-surface-hover px-2 py-0.5 text-darkgrey">
              {file.extension?.toUpperCase()?.replace(/^\./, "") || file.category || "FILE"}
            </span>
            <span>{formatBytes(file.size_bytes)}</span>
            <span>{formatDate(file.created_at)}</span>
          </div>
          <p className="mt-1 text-xs text-grey truncate">{file.uploaded_by_name || "Unknown uploader"}</p>
        </div>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu
            file={file}
            permissions={permissions}
            onPreview={onPreview}
            onDownload={onDownload}
            onShare={onShare}
            onDelete={onDelete}
            onMove={onMove}
            onRename={onRename}
          />
        </div>
      </div>
    </article>
  );
};

const TypeBadge = ({ category, extension }) => {
  const ext = extension?.toUpperCase()?.replace(/^\./, "");
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-hover text-darkgrey border border-surface-border">
      {ext || category || "FILE"}
    </span>
  );
};

const SortHeaderArrows = ({ columnKey, sortBy, sortOrder }) => {
  const isActive = sortBy === columnKey;
  return (
    <span className="inline-flex flex-col items-center justify-center ml-1 -space-y-0.5">
      <HiOutlineChevronUp
        className={cn(
          "w-3 h-3",
          isActive && sortOrder === "asc" ? "text-secondary-dark" : "text-grey"
        )}
      />
      <HiOutlineChevronDown
        className={cn(
          "w-3 h-3",
          isActive && sortOrder === "desc" ? "text-secondary-dark" : "text-grey"
        )}
      />
    </span>
  );
};

const FileRow = ({ file, permissions, onPreview, onShare, onDelete, onDownload, onSelect, isSelected, onMove, onRename }) => {
  return (
    <tr
      onClick={(e) => {
        if (e.target.closest("[data-row-action]")) return;
        onSelect?.(file);
      }}
      className={cn(
        "border-b border-surface-border transition-colors group cursor-pointer",
        isSelected ? "bg-primary/5" : "hover:bg-surface-hover/30"
      )}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(file);
            }}
            data-row-action
            className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
            aria-label={`Preview ${file.original_name}`}
          >
            <FileIcon category={file.category} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPreview(file);
            }}
            data-row-action
            className="text-sm font-medium text-darkgrey hover:text-primary transition-colors text-left truncate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:rounded"
          >
            {file.original_name}
          </button>
        </div>
      </td>
      <td className="py-3 px-4">
        <TypeBadge category={file.category} extension={file.extension} />
      </td>
      <td className="py-3 px-4 text-sm text-darkgrey whitespace-nowrap">
        {formatBytes(file.size_bytes)}
      </td>
      <td className="py-3 px-4 text-sm text-darkgrey whitespace-nowrap">
        {formatDate(file.created_at)}
      </td>
      <td className="py-3 px-4 text-sm text-darkgrey">
        {file.uploaded_by_name || "—"}
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center justify-end pr-2">
          <RowActionsMenu
            file={file}
            permissions={permissions}
            onPreview={onPreview}
            onDownload={onDownload}
            onShare={onShare}
            onDelete={onDelete}
            onMove={onMove}
            onRename={onRename}
          />
        </div>
      </td>
    </tr>
  );
};

const SkeletonRow = () => (
  <tr className="border-b border-surface-border">
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <Skeleton className="w-8 h-10 rounded" />
        <Skeleton className="w-40 h-4 rounded" />
      </div>
    </td>
    <td className="py-3 px-4"><Skeleton className="w-12 h-4 rounded" /></td>
    <td className="py-3 px-4"><Skeleton className="w-16 h-4 rounded" /></td>
    <td className="py-3 px-4"><Skeleton className="w-24 h-4 rounded" /></td>
    <td className="py-3 px-4"><Skeleton className="w-20 h-4 rounded" /></td>
    <td className="py-3 px-4"><Skeleton className="w-8 h-4 rounded" /></td>
  </tr>
);

const FolderIcon = () => (
  <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-secondary">
    <HiOutlineFolder className="w-5 h-5 text-white" />
  </div>
);

const FolderRow = ({ folder, onOpen, onRename, onMove, onDelete, permissions }) => {
  const canManage = permissions?.can_manage_folders !== false;
  const canDelete = permissions?.can_delete;
  const fileCount = folder.file_count ?? 0;
  const itemsLabel = fileCount === 1 ? "1 file" : `${fileCount} files`;
  return (
    <tr
      onClick={(e) => {
        if (e.target.closest("[data-row-action]")) return;
        onOpen?.(folder);
      }}
      className="border-b border-surface-border transition-colors cursor-pointer hover:bg-surface-hover/40"
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3 min-w-0">
          <FolderIcon />
          <div className="min-w-0">
            <button
              data-row-action
              onClick={(e) => {
                e.stopPropagation();
                onOpen?.(folder);
              }}
              className="block text-sm font-semibold text-darkgrey hover:text-primary transition-colors text-left truncate w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:rounded"
            >
              {folder.name}
            </button>
            <span className="block text-xs text-grey truncate">{itemsLabel}</span>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-grey whitespace-nowrap">—</td>
      <td className="py-3 px-4 text-sm text-grey whitespace-nowrap">—</td>
      <td className="py-3 px-4 text-sm text-darkgrey whitespace-nowrap">{formatDate(folder.created_at)}</td>
      <td className="py-3 px-4 text-sm text-darkgrey">—</td>
      <td className="py-3 px-4">
        <div className="flex items-center justify-end gap-1 pr-2">
          {canManage && (
            <RowActionsMenu
              file={{ ...folder, original_name: folder.name }}
              permissions={permissions}
              onPreview={() => onOpen?.(folder)}
              onRename={onRename ? () => onRename({ ...folder, kind: "folder" }) : undefined}
              onMove={onMove ? () => onMove({ ...folder, kind: "folder" }) : undefined}
              onDelete={canDelete ? () => onDelete?.(folder) : undefined}
              secondaryItems={[]}
              itemKind="folder"
            />
          )}
        </div>
      </td>
    </tr>
  );
};

const MobileFolderCard = ({ folder, onOpen, onRename, onMove, onDelete, permissions }) => {
  const canManage = permissions?.can_manage_folders !== false;
  const canDelete = permissions?.can_delete;
  const fileCount = folder.file_count ?? 0;
  const itemsLabel = fileCount === 1 ? "1 file" : `${fileCount} files`;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(folder)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen?.(folder);
        }
      }}
      className="flex w-full items-center gap-3 rounded-xl border border-surface-border bg-white p-3 text-left shadow-sm transition-colors hover:border-secondary/40 hover:bg-secondary/[0.02] focus:outline-none focus:ring-2 focus:ring-secondary/20"
    >
      <FolderIcon />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-darkgrey">{folder.name}</p>
        <p className="text-xs text-grey">Folder · {itemsLabel} · {formatDate(folder.created_at)}</p>
      </div>
      {canManage && (
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <RowActionsMenu
            file={{ ...folder, original_name: folder.name }}
            permissions={permissions}
            onPreview={() => onOpen?.(folder)}
            onRename={onRename ? () => onRename({ ...folder, kind: "folder" }) : undefined}
            onMove={onMove ? () => onMove({ ...folder, kind: "folder" }) : undefined}
            onDelete={canDelete ? () => onDelete?.(folder) : undefined}
            secondaryItems={[]}
            itemKind="folder"
          />
        </div>
      )}
    </div>
  );
};

export const FileList = ({
  files = [],
  folders = [],
  count,
  page,
  totalPages,
  isLoading,
  showToolbar = true,
  searchQuery,
  filterType,
  sortBy,
  sortOrder,
  permissions,
  selectedFileId,
  onSearchChange,
  onFilterChange,
  onSortChange,
  onSortSet,
  onPageChange,
  onSelectFile,
  onPreview,
  onDownload,
  onShare,
  onDelete,
  onOpenFolder,
  onRenameItem,
  onMoveFolder,
  onMoveFile,
  onDeleteFolder,
  currentFolderName,
  onUpload,
}) => {
  const showEmpty = !isLoading && files.length === 0 && folders.length === 0;
  const [sortOpen, setSortOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);

  const showUploadPrompt = useMemo(() => {
    if (!showEmpty) return false;
    if (!searchQuery && !filterType) return true;
    return false;
  }, [showEmpty, searchQuery, filterType]);

  const activeTypeLabel = useMemo(() => {
    const opt = TYPE_OPTIONS.find((o) => o.key === filterType);
    return opt?.label || "All";
  }, [filterType]);

  const startItem = (page - 1) * 50 + 1;
  const endItem = Math.min(page * 50, count);

  return (
    <div className={cn("w-full bg-white rounded-xl border border-surface-border p-4", !showToolbar && "pt-3")}>
      {/* Search, Filters, Sort */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap flex-1">
          <div className="relative flex-1 max-w-xs">
            <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search files and folders..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-surface-border rounded-lg bg-white focus:border-primary/50 focus:ring-2 focus:ring-primary/10 outline-none transition-all"
              aria-label="Search files and folders"
            />
          </div>
          <button
            onClick={() => onFilterChange("")}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-full border transition-colors",
              !filterType
                ? "bg-secondary/10 text-secondary-dark border-secondary/30"
                : "bg-white text-darkgrey border-surface-border hover:bg-surface-hover"
            )}
            aria-pressed={!filterType}
          >
            All
          </button>
          <CustomPopover
            open={typeOpen}
            setOpen={setTypeOpen}
            placement="bottom-start"
            trigger={
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-surface-border rounded-full text-xs font-medium text-darkgrey hover:bg-surface-hover transition-colors">
                <span>Type</span>
                <HiOutlineChevronDown className="w-3 h-3" />
              </button>
            }
          >
            <div className="flex flex-col py-1 min-w-[140px]">
              {TYPE_OPTIONS.filter((o) => o.key !== "").map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => {
                    onFilterChange(opt.key);
                    setTypeOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm font-sans transition-colors cursor-pointer",
                    filterType === opt.key
                      ? "bg-secondary/10 text-secondary-dark font-semibold"
                      : "text-darkgrey hover:bg-surface-hover"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </CustomPopover>
          {filterType && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary-dark border border-secondary/20">
              {activeTypeLabel}
              <button
                onClick={() => onFilterChange("")}
                className="hover:text-primary transition-colors"
                aria-label="Clear type filter"
              >
                ×
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="md:hidden">
            <CustomPopover
              open={sortOpen}
              setOpen={setSortOpen}
              placement="bottom-end"
              trigger={
                <button
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-surface-border rounded-lg text-sm font-sans text-darkgrey hover:bg-surface-hover transition-colors"
                  aria-label="Sort files"
                >
                  <HiOutlineArrowsUpDown className="w-4 h-4" />
                  <span>Sort</span>
                </button>
              }
            >
              <div className="flex flex-col py-1 min-w-[160px]">
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={`${opt.field}-${opt.order}`}
                    onClick={() => {
                      onSortSet?.(opt.field, opt.order);
                      setSortOpen(false);
                    }}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm font-sans transition-colors cursor-pointer",
                      sortBy === opt.field && sortOrder === opt.order
                        ? "bg-secondary/10 text-secondary-dark font-semibold"
                        : "text-darkgrey hover:bg-surface-hover"
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </CustomPopover>
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {isLoading ? (
          Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="rounded-xl border border-surface-border bg-white p-4">
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
          ))
        ) : showEmpty ? (
          <div className="py-12 text-center">
            {showUploadPrompt ? (
              <EmptyState
                compact
                className="border-0"
                icon={HiOutlineFolderOpen}
                title={currentFolderName ? `No files in ${currentFolderName}` : "No files uploaded yet"}
                description="Drag and drop files here, or upload them directly."
                actionText={permissions?.can_upload && onUpload ? "Upload files" : undefined}
                onAction={permissions?.can_upload && onUpload ? onUpload : undefined}
              />
            ) : (
              <EmptyState title="No files found" description="No files match your search" compact />
            )}
          </div>
        ) : (
          <>
            {folders.map((folder) => (
              <MobileFolderCard
                key={`folder-${folder.id}`}
                folder={folder}
                onOpen={onOpenFolder}
                onRename={onRenameItem}
                onMove={onMoveFolder}
                onDelete={onDeleteFolder}
                permissions={permissions}
              />
            ))}
            {files.map((file) => (
              <MobileFileCard
                key={file.id}
                file={file}
                permissions={permissions}
                onSelect={onSelectFile}
                onPreview={onPreview}
                onDownload={onDownload}
                onShare={onShare}
                onDelete={onDelete}
                onMove={onMoveFile}
                onRename={onRenameItem}
                isSelected={selectedFileId === file.id}
              />
            ))}
          </>
        )}
      </div>

      {/* Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-surface-border">
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && onSortChange(col.key)}
                  className={cn(
                    "py-3 px-4 text-left text-xs font-semibold text-darkgrey uppercase tracking-wider select-none transition-colors",
                    col.sortable && "cursor-pointer hover:text-primary"
                  )}
                  aria-sort={col.sortable && sortBy === col.key ? (sortOrder === "asc" ? "ascending" : "descending") : "none"}
                  tabIndex={col.sortable ? 0 : -1}
                  onKeyDown={(e) => col.sortable && (e.key === "Enter" || e.key === " ") && onSortChange(col.key)}
                >
                  <span className="inline-flex items-center">
                    {col.label}
                    {col.sortable && (
                      <SortHeaderArrows
                        columnKey={col.key}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }, (_, i) => <SkeletonRow key={i} />)
            ) : showEmpty ? (
              <tr>
                <td colSpan={6} className="py-16 text-center">
                  {showUploadPrompt ? (
                    <EmptyState
                      compact
                      className="border-0"
                      icon={HiOutlineFolderOpen}
                      title={currentFolderName ? `No files in ${currentFolderName}` : "No files uploaded yet"}
                      description="Drag and drop files here, or upload them directly."
                      actionText={permissions?.can_upload && onUpload ? "Upload files" : undefined}
                      onAction={permissions?.can_upload && onUpload ? onUpload : undefined}
                    />
                  ) : (
                    <EmptyState title="No files found" description="No files match your search" compact />
                  )}
                </td>
              </tr>
            ) : (
              <>
                {folders.map((folder) => (
                  <FolderRow
                    key={`folder-${folder.id}`}
                    folder={folder}
                    onOpen={onOpenFolder}
                    onRename={onRenameItem}
                    onMove={onMoveFolder}
                    onDelete={onDeleteFolder}
                    permissions={permissions}
                  />
                ))}
                {files.map((file) => (
                <FileRow
                  key={file.id}
                  file={file}
                  permissions={permissions}
                  onSelect={onSelectFile}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  onShare={onShare}
                  onDelete={onDelete}
                  onMove={onMoveFile}
                  onRename={onRenameItem}
                  isSelected={selectedFileId === file.id}
                />
              ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && files.length > 0 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-surface-border">
          <p className="text-sm text-grey">
            Showing {startItem} to {endItem} of {count} file{count !== 1 ? "s" : ""}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-surface-border text-darkgrey hover:bg-surface-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                <HiOutlineChevronLeft className="w-4 h-4" />
              </button>
              {buildPageWindow(page, totalPages).map((p, i) => (
                <button
                  key={p === "..." ? `dots-${i}` : p}
                  onClick={() => p !== "..." && onPageChange(p)}
                  aria-current={p === page ? "page" : undefined}
                  className={cn(
                    "w-8 h-8 text-xs font-medium rounded-md transition-colors",
                    p === page
                      ? "bg-secondary/10 text-secondary-dark border border-secondary/30"
                      : p === "..."
                      ? "text-grey cursor-default"
                      : "text-darkgrey hover:bg-surface-active border border-surface-border"
                  )}
                  disabled={p === "..."}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-surface-border text-darkgrey hover:bg-surface-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                <HiOutlineChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
